/**
 * The single FHIR namespace authority for all Eluve-minted systems — the ONE
 * definition (ENG-5115); `@eluve/nestjs-medplum`, `@eluve/plan-shapes`, and
 * `@eluve/fhir-client` all re-export from here.
 *
 * A FHIR `Identifier.system` (and a `meta.tag` `Coding.system`) names the
 * *assigning authority* — "Eluve mints this value" — which is a stable logical
 * fact in every environment. It is therefore a CONSTANT, never derived from a
 * deployment/request URL: environment isolation comes from separate Medplum
 * servers + per-tenant Project scoping, not from the namespace string. Deriving
 * it from the host would silently break conditional upsert any time data moves
 * between environments (DR restore, prod→staging clone, tenant migration,
 * domain rename).
 *
 * Grammar conventions on top of this base (match `@eluve/plan-shapes`):
 * - extension URLs:        `<ns>/StructureDefinition/<name>`
 * - coding systems:        `<ns>/CodeSystem/<name>`
 * - identifier systems:    `<ns>/<entity>-id` (pathless, pre-dates the grammar;
 *   byte-compatibility with stored identifiers forbids changing it)
 * - marker/tag systems:    `<ns>/<name>` (pathless, same reason)
 */
export const ELUVE_FHIR_NAMESPACE = 'https://eluve.com';

/**
 * The mirror's durable key for a Location: `<ns>/location-id|<eluve id>`.
 * Mirrors the backend's `buildIdentifierSystem('location')` — same string,
 * declared here because the frontend can't reach `@eluve/nestjs-medplum`.
 */
export const FHIR_LOCATION_ID_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/location-id`;

/**
 * The practitioner mirror's durable key: `<ns>/user-id|<eluve user id>`.
 * The value is the USER id — the same person in two tenants is two
 * Practitioners (one per Medplum project) carrying the same identifier.
 * Mirrors the backend's `buildIdentifierSystem('user')`.
 */
export const FHIR_USER_ID_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/user-id`;

/**
 * Identifier system for a member's PER-LOCATION PractitionerRole:
 * `<ns>/user-location-id|<userId>:<locationId>` (compound value via
 * {@link userLocationIdentifierValue}). Mirrors the backend's
 * `buildIdentifierSystem('user-location')`.
 */
export const FHIR_USER_LOCATION_ID_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/user-location-id`;

/** The compound identifier value for a (user, location) PractitionerRole. */
export function userLocationIdentifierValue(
  userId: string,
  locationId: string,
): string {
  return `${userId}:${locationId}`;
}
